import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useTranslation } from "../../../localization/useTranslation";
import { useUrlProvider } from "../../../useUrlProvider";
import { StepTypeEnum } from "../../ProcessingActivity.types";
import { ControllerProcess, ProcessingActivitySharingsViewModel, SharingRowModel, SharingTypeEnum } from "../ProcessingActivityOverview.types";
import { IProcessingActivitySharingsTab } from "./ProcessingActivitySharingsTab";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { sortByName } from "@common/hooks/filterUtils.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { useSharingFilterData } from "../../components/sharingsTable/SharingsTable.hooks";
import { useGroupEntitiesForUserQuery } from "@common/hooks/useSelectableItemQueries.ts";
import { useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

// The overview reads the same query for the tab badge while the tab panel is not rendered; the shared key makes React Query serve both from one request.
export function useProcessingActivitySharingsQuery(processingActivityId: string | undefined) {
    return useDotLegalQueryWithKey<ProcessingActivitySharingsViewModel>(
        "processingActivitySharings" + processingActivityId,
        `/ProcessingActivityOverview/${processingActivityId}/sharings`
    );
}

export default function useProcessingActivitySharingsTabHooks(props: IProcessingActivitySharingsTab) {
    const { translateString } = useTranslation();
    const { getSharingTypeFilterData, getLevelOfProtectionFilterData } = useSharingFilterData();
    const { permissions } = useUserContext();
    const { getEditProcessingActivityUrl } = useUrlProvider();

    const groupEntitiesForUserQuery = useGroupEntitiesForUserQuery();
    const { isLoading, data } = useProcessingActivitySharingsQuery(props.processingActivityId);
    const sharings = data?.sharings ?? [];
    const senders = data?.senders ?? [];
    const recipients = data?.recipients ?? [];
    const dataCategories = data?.dataCategories ?? [];

    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `processingActivity-${props.processingActivityId}-sharings-filters`
    );

    const partyIds = new Set(sharings.flatMap((sharing) => [sharing.senderId, sharing.recipientId]));
    const groupEntitiesOnSharings = (groupEntitiesForUserQuery.data ?? []).filter((groupEntity) => partyIds.has(groupEntity.id));
    // With a single group company every sharing involves it, so there is nothing to choose between.
    const canFilterOnGroupEntity = groupEntitiesOnSharings.length > 1;
    // A filter stored while there were several group companies must not keep hiding rows once its option is gone.
    const groupEntityFilterMappings = canFilterOnGroupEntity
        ? [{ id: "groupEntity", candidate: (sharing: SharingRowModel) => [sharing.senderId, sharing.recipientId] }]
        : [];

    const filteredSharings = applyClientFilters(
        sharings,
        [
            ...groupEntityFilterMappings,
            { id: "sharingType", candidate: (sharing) => getSharingTypes(sharing).map((type) => type.toString()) },
            { id: "sender", candidate: (sharing) => sharing.senderId },
            { id: "dataCategory", candidate: (sharing) => sharing.dataCategories.map((dataCategory) => dataCategory.id) },
            { id: "recipient", candidate: (sharing) => sharing.recipientId },
            { id: "levelOfProtection", candidate: (sharing) => sharing.recipientLevelOfProtection.toString() },
        ],
        selectFilters,
        {},
        filterOperators
    );

    // A sharing can come from more than one origin, so it can belong to several sharing types at once.
    function getSharingTypes(sharing: SharingRowModel): Array<SharingTypeEnum> {
        const sharingTypes: Array<SharingTypeEnum> = [];

        if (sharing.sharingOrigin.systemId || sharing.sharingOrigin.dataProcessorId) {
            sharingTypes.push(SharingTypeEnum.dataProcessor);
        }
        if (sharing.sharingOrigin.transferId) {
            sharingTypes.push(SharingTypeEnum.disclosure);
        }
        if (sharing.sharingOrigin.sourceId) {
            sharingTypes.push(SharingTypeEnum.source);
        }
        if (sharing.sharingOrigin.dataControllerId) {
            sharingTypes.push(SharingTypeEnum.dataController);
        }
        if (sharing.sharingOrigin.jointDataControllerId) {
            sharingTypes.push(SharingTypeEnum.jointDataController);
        }

        return sharingTypes;
    }

    function getFilterOptions(): Array<FilterOption> {
        const groupEntityOptions: Array<FilterOption> = canFilterOnGroupEntity
            ? [
                  {
                      id: "groupEntity",
                      label: translateString("groupEntities"),
                      type: FilterOptionType.SelectField,
                      value: selectFilters.groupEntity ?? [],
                      fixedData: [...groupEntitiesOnSharings].sort(sortByName),
                  },
              ]
            : [];

        return withOperators(
            [
                ...groupEntityOptions,
                {
                    id: "sharingType",
                    label: translateString("sharingType"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.sharingType ?? [],
                    fixedData: getSharingTypeFilterData(sharings.flatMap(getSharingTypes)),
                },
                {
                    id: "sender",
                    label: translateString("Sender"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.sender ?? [],
                    fixedData: [...senders].sort(sortByName),
                },
                {
                    id: "dataCategory",
                    label: translateString("dataCategoryHeader"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.dataCategory ?? [],
                    fixedData: [...dataCategories].sort(sortByName),
                },
                {
                    id: "recipient",
                    label: translateString("recipient"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.recipient ?? [],
                    fixedData: [...recipients].sort(sortByName),
                },
                {
                    id: "levelOfProtection",
                    label: translateString("sharingsTo"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.levelOfProtection ?? [],
                    fixedData: getLevelOfProtectionFilterData(sharings.map((sharing) => sharing.recipientLevelOfProtection)),
                },
            ],
            filterOperators
        );
    }

    const rows = (filteredSharings ?? []).map((sharing) => ({
        ...sharing,
        senderRole: translateString(ControllerProcess[Number(sharing.senderRole)]),
        recipientRole: translateString(ControllerProcess[Number(sharing.recipientRole)]),
        dataCategoriesString: sharing.dataCategories.map((x) => x.name).join(", "),
    }));

    function getSharingLink(id: string, step: StepTypeEnum, subStep: StepTypeEnum | undefined, subId: string) {
        return getEditProcessingActivityUrl(id, step, subStep, subId);
    }

    function hasAccessToSharing(subStep: StepTypeEnum | undefined) {
        if (subStep === StepTypeEnum.sources) {
            return permissions.processingActivitySourcesPermissions?.permissions.read;
        } else if (subStep === StepTypeEnum.disclosure) {
            return permissions.processingActivityDisclosuresPermissions?.permissions.read;
        } else if (subStep === StepTypeEnum.dataController) {
            return permissions.processingActivityDataControllersPermissions?.permissions.read;
        } else if (subStep === StepTypeEnum.dataprocessor) {
            return permissions.processingActivityDataProcessorsPermissions?.permissions.read;
        } else {
            return permissions.processingActivityJointDataControllerPermissions?.permissions.read;
        }
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0);

    return {
        rows,
        isLoading,
        hasSharings: isLoading || sharings.length > 0,
        isFilterBarLoading: isLoading || groupEntitiesForUserQuery.isLoading,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        getSharingLink,
        hasAccessToSharing,
    };
}
